import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { FlaskConical, Loader2, Search, Send, CheckCircle, CreditCard, Clock, XCircle, CalendarIcon, Ban } from 'lucide-react';
import { toast } from 'sonner';

const fmtINR = (paise) => `₹${((paise || 0) / 100).toLocaleString('en-IN')}`;

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
const toYmd = (d) => d ? new Date(d).toISOString().slice(0, 10) : '';
const serializeRange = (range) => {
  if (!range) return '';
  if (range.from && range.to && toYmd(range.from) !== toYmd(range.to)) return `${toYmd(range.from)} to ${toYmd(range.to)}`;
  if (range.from) return toYmd(range.from);
  return '';
};

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function IndustryLabs({ API, token, user }) {
  const [tab, setTab] = useState('browse');
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [bookOpen, setBookOpen] = useState(null); // selected lab object
  const [form, setForm] = useState({ purpose: '', requested_dates: '', hours: 0, days: 0 });
  const [dateRange, setDateRange] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);

  // My bookings
  const [bookings, setBookings] = useState([]);
  const [loadingBk, setLoadingBk] = useState(true);
  const [paying, setPaying] = useState(null);

  const fetchLabs = useCallback(async (query = '') => {
    setLoading(true);
    const url = `${API}/labs${query ? `?q=${encodeURIComponent(query)}` : ''}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setLabs((await res.json()).labs || []);
    setLoading(false);
  }, [API, token]);

  const fetchBookings = useCallback(async () => {
    setLoadingBk(true);
    const res = await fetch(`${API}/industry/lab-bookings`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setBookings((await res.json()).bookings || []);
    setLoadingBk(false);
  }, [API, token]);

  useEffect(() => { fetchLabs(''); fetchBookings(); }, [fetchLabs, fetchBookings]);

  const openBook = (lab) => {
    setBookOpen(lab);
    setForm({ purpose: '', requested_dates: '', hours: 0, days: 0 });
    setDateRange(undefined);
  };

  const estAmount = bookOpen
    ? (Number(form.hours||0) * (bookOpen.rate_per_hour_inr || 0) + Number(form.days||0) * (bookOpen.rate_per_day_inr || 0))
    : 0;

  const submitBooking = async (e) => {
    e.preventDefault();
    if ((form.purpose || '').trim().length < 10) { toast.error('Add a short purpose (min 10 chars)'); return; }
    const serialized = serializeRange(dateRange);
    if (!serialized) { toast.error('Pick one or more dates'); return; }
    if (!Number(form.hours) && !Number(form.days)) { toast.error('Enter hours and/or days'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/labs/${bookOpen.id}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ lab_id: bookOpen.id, purpose: form.purpose, requested_dates: serialized, hours: Number(form.hours)||0, days: Number(form.days)||0 }),
      });
      if (res.ok) { toast.success('Booking requested. The university will review.'); setBookOpen(null); fetchBookings(); setTab('bookings'); }
      else { const d = await res.json().catch(() => ({})); toast.error(d.detail || 'Failed'); }
    } finally { setSubmitting(false); }
  };

  const payBooking = async (b) => {
    setPaying(b.id);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error('Payment SDK failed'); return; }
      const or = await fetch(`${API}/lab-bookings/${b.id}/payment/create-order`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!or.ok) { const d = await or.json().catch(() => ({})); toast.error(d.detail || 'Could not create order'); return; }
      const order = await or.json();
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'CollabHive',
        description: `Lab booking — ${b.lab_name}`,
        order_id: order.order_id,
        prefill: { email: user?.email, name: user?.company_name || '' },
        theme: { color: '#10b981' },
        handler: async (resp) => {
          const vr = await fetch(`${API}/lab-bookings/${b.id}/payment/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(resp),
          });
          if (vr.ok) { toast.success('Payment successful'); fetchBookings(); }
          else { toast.error('Payment verification failed'); }
        },
      });
      rzp.open();
    } finally { setPaying(null); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 border-b">
        <button className={`px-3 py-2 text-sm ${tab==='browse' ? 'border-b-2 border-primary font-medium' : 'text-muted-foreground'}`} onClick={() => setTab('browse')} data-testid="labs-tab-browse">Browse Labs</button>
        <button className={`px-3 py-2 text-sm ${tab==='bookings' ? 'border-b-2 border-primary font-medium' : 'text-muted-foreground'}`} onClick={() => setTab('bookings')} data-testid="labs-tab-bookings">My Bookings</button>
      </div>

      {tab === 'browse' && (
        <>
          <div className="flex gap-2">
            <Input placeholder="Search labs, areas of expertise, equipment, universities…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key==='Enter' && fetchLabs(q)} data-testid="labs-search" />
            <Button onClick={() => fetchLabs(q)} className="gap-2" data-testid="labs-search-btn"><Search className="w-4 h-4" /> Search</Button>
          </div>
          {loading ? <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto my-6" /> :
           !labs.length ? <p className="text-sm text-muted-foreground py-6 text-center">No labs matching your search.</p> :
            <div className="space-y-3">
              {labs.map(l => (
                <Card key={l.id} data-testid={`browse-lab-${l.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <h4 className="font-medium flex items-center gap-2"><FlaskConical className="w-4 h-4 text-primary" /> {l.name}</h4>
                        <p className="text-xs text-muted-foreground">{l.university_name}</p>
                        <p className="text-sm mt-2">{l.area_of_expertise}</p>
                        {(l.equipment && l.equipment.length > 0) ? (
                          <div className="mt-2">
                            <p className="text-xs text-muted-foreground mb-1"><b>Equipment:</b></p>
                            <div className="flex flex-wrap gap-1">
                              {l.equipment.map((eq, idx) => (
                                <Badge key={idx} variant="outline" className="text-[10px]">{eq}</Badge>
                              ))}
                            </div>
                          </div>
                        ) : l.equipment_available ? (
                          <p className="text-xs text-muted-foreground mt-2"><b>Equipment:</b> {l.equipment_available}</p>
                        ) : null}
                        <div className="flex gap-2 flex-wrap mt-2">
                          {l.rate_per_hour_inr>0 && <Badge variant="secondary">₹{l.rate_per_hour_inr}/hr</Badge>}
                          {l.rate_per_day_inr>0 && <Badge variant="secondary">₹{l.rate_per_day_inr}/day</Badge>}
                          {l.capacity && <Badge variant="outline">{l.capacity}</Badge>}
                        </div>
                      </div>
                      {l.booking_disabled ? (
                        <div className="flex flex-col items-end gap-1">
                          <Button size="sm" disabled className="gap-2" data-testid={`request-lab-disabled-${l.id}`}>
                            <Ban className="w-4 h-4" /> Booking Unavailable
                          </Button>
                          <span className="text-[10px] text-amber-700">Bookings are disabled for this university</span>
                        </div>
                      ) : (
                        <Button size="sm" onClick={() => openBook(l)} className="gap-2" data-testid={`request-lab-${l.id}`}>
                          <Send className="w-4 h-4" /> Request Access
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          }
        </>
      )}

      {tab === 'bookings' && (
        loadingBk ? <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto my-6" /> :
        !bookings.length ? <p className="text-sm text-muted-foreground py-6 text-center">No bookings yet. Request access to a lab above.</p> :
        <div className="space-y-3">
          {bookings.map(b => (
            <Card key={b.id} data-testid={`industry-booking-${b.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h4 className="font-medium">{b.lab_name}</h4>
                    <p className="text-xs text-muted-foreground">{b.university_name} · {new Date(b.created_at).toLocaleString()}</p>
                  </div>
                  <StatusBadge status={b.status} />
                </div>
                <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{b.purpose}</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mt-3 pt-2 border-t">
                  <div><p className="text-[10px] text-muted-foreground">Dates</p><p className="font-medium">{b.requested_dates}</p></div>
                  <div><p className="text-[10px] text-muted-foreground">Hours</p><p className="font-medium">{b.hours}</p></div>
                  <div><p className="text-[10px] text-muted-foreground">Days</p><p className="font-medium">{b.days}</p></div>
                  <div><p className="text-[10px] text-muted-foreground">Amount</p><p className="font-medium">{fmtINR(b.amount_paise)}</p></div>
                </div>
                {b.status === 'approved' && b.booking_disabled && (
                  <p className="text-xs text-amber-700 mt-3 flex items-center gap-1" data-testid={`pay-lab-disabled-${b.id}`}><Ban className="w-3 h-3" /> Payments are paused — bookings are disabled for this university</p>
                )}
                {b.status === 'approved' && !b.booking_disabled && (
                  <Button size="sm" onClick={() => payBooking(b)} disabled={paying===b.id} className="gap-2 mt-3" data-testid={`pay-lab-${b.id}`}>
                    {paying===b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                    Pay {fmtINR(b.amount_paise)}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!bookOpen} onOpenChange={(v) => !v && setBookOpen(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          {bookOpen && (
            <>
              <DialogHeader>
                <DialogTitle>Request access — {bookOpen.name}</DialogTitle>
                <DialogDescription>{bookOpen.university_name}</DialogDescription>
              </DialogHeader>
              <form onSubmit={submitBooking} className="space-y-3">
                <div>
                  <label className="text-xs font-medium">Purpose</label>
                  <Textarea rows={3} value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} placeholder="Why do you need this lab? What experiments will you run?" data-testid="book-purpose" />
                </div>
                <div>
                  <label className="text-xs font-medium">Requested dates</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" className="w-full justify-start text-left font-normal mt-1" data-testid="book-dates-trigger">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {dateRange?.from ? (
                          dateRange.to ? `${fmtDate(dateRange.from)} – ${fmtDate(dateRange.to)}` : fmtDate(dateRange.from)
                        ) : (
                          <span className="text-muted-foreground">Pick a date or range</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="range"
                        selected={dateRange}
                        onSelect={setDateRange}
                        numberOfMonths={1}
                        disabled={{ before: new Date() }}
                        data-testid="book-dates-calendar"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium">Hours (₹{bookOpen.rate_per_hour_inr || 0}/hr)</label>
                    <Input type="number" min="0" value={form.hours} onChange={e => setForm({ ...form, hours: e.target.value })} data-testid="book-hours" />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Days (₹{bookOpen.rate_per_day_inr || 0}/day)</label>
                    <Input type="number" min="0" value={form.days} onChange={e => setForm({ ...form, days: e.target.value })} data-testid="book-days" />
                  </div>
                </div>
                <div className="rounded-md border p-3 bg-muted/40 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Estimated amount</span>
                  <span className="text-lg font-bold">₹{estAmount.toLocaleString('en-IN')}</span>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={submitting || estAmount <= 0} className="gap-2" data-testid="book-submit">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Submit Request
                  </Button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { icon: Clock, cls: 'bg-amber-500/10 text-amber-700' },
    approved: { icon: CheckCircle, cls: 'bg-blue-500/10 text-blue-700' },
    rejected: { icon: XCircle, cls: 'bg-zinc-500/10 text-zinc-700' },
    paid: { icon: CheckCircle, cls: 'bg-emerald-500/10 text-emerald-700' },
  };
  const { icon: Icon, cls } = map[status] || map.pending;
  return <Badge className={cls}><Icon className="w-3 h-3 mr-1" />{status}</Badge>;
}
